import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { Save, Plus, Minus } from 'lucide-react';
import { CashCountFormProps } from './CashCountForm.types';
import { BILL_DENOMINATIONS, COIN_DENOMINATIONS } from '../../types/cash-register.types';
import { NumericKeypad } from '@/components/ui/keypads';
import { toast } from 'sonner';

export const CashCountForm: React.FC<CashCountFormProps> = ({ cashCount, onChange, onSave }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [currentEditingField, setCurrentEditingField] = useState<{
    type: 'bill' | 'coin';
    denomination: number;
    title: string;
  } | null>(null);
  const formatCurrency = (amount: number | null | undefined): string => {
    const safeAmount = amount || 0;
    return `S/ ${safeAmount.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleSave = async () => {
    if (!onSave) {
      // Fallback to localStorage if no onSave prop provided
      const cashData = {
        bills: cashCount.bills,
        coins: cashCount.coins,
        totalCounted: cashCount.totalCounted,
        timestamp: new Date().toISOString(),
      };
      
      localStorage.setItem('cashCount', JSON.stringify(cashData));
      
      onChange({
        ...cashCount,
        isSaved: true
      });
      
      toast.success('Conteo guardado exitosamente');
      return;
    }

    try {
      setIsSaving(true);
      await onSave(cashCount);
      toast.success('Conteo guardado exitosamente');
    } catch (error) {
      console.error('Error saving cash count:', error);
      toast.error('Error al guardar el conteo. Inténtalo de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBillChange = (denomination: number, value: string) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    const updatedBills = { ...cashCount.bills, [denomination.toString()]: numValue };
    
    const newTotalCounted = calculateTotal(updatedBills, cashCount.coins);
    
    onChange({
      ...cashCount,
      bills: updatedBills,
      totalCounted: newTotalCounted,
    });
  };

  const handleCoinChange = (denomination: number, value: string) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    const updatedCoins = { ...cashCount.coins, [denomination.toString()]: numValue };
    
    const newTotalCounted = calculateTotal(cashCount.bills, updatedCoins);
    
    onChange({
      ...cashCount,
      coins: updatedCoins,
      totalCounted: newTotalCounted,
    });
  };

  const incrementBill = (denomination: number) => {
    const currentValue = cashCount.bills[denomination.toString()] || 0;
    handleBillChange(denomination, (currentValue + 1).toString());
  };

  const decrementBill = (denomination: number) => {
    const currentValue = cashCount.bills[denomination.toString()] || 0;
    if (currentValue > 0) {
      handleBillChange(denomination, (currentValue - 1).toString());
    }
  };

  const incrementCoin = (denomination: number) => {
    const currentValue = cashCount.coins[denomination.toString()] || 0;
    handleCoinChange(denomination, (currentValue + 1).toString());
  };

  const decrementCoin = (denomination: number) => {
    const currentValue = cashCount.coins[denomination.toString()] || 0;
    if (currentValue > 0) {
      handleCoinChange(denomination, (currentValue - 1).toString());
    }
  };

  const openKeypadForBill = (denomination: number) => {
    setCurrentEditingField({
      type: 'bill',
      denomination,
      title: `Ingresa cantidad de billetes de S/ ${denomination}`
    });
    setKeypadOpen(true);
  };

  const openKeypadForCoin = (denomination: number) => {
    setCurrentEditingField({
      type: 'coin',
      denomination,
      title: `Ingresa cantidad de monedas de S/ ${denomination}`
    });
    setKeypadOpen(true);
  };

  const handleKeypadSubmit = (value: number) => {
    if (!currentEditingField) return;

    if (currentEditingField.type === 'bill') {
      handleBillChange(currentEditingField.denomination, value.toString());
    } else {
      handleCoinChange(currentEditingField.denomination, value.toString());
    }
  };

  const getCurrentValue = () => {
    if (!currentEditingField) return 0;
    
    if (currentEditingField.type === 'bill') {
      return cashCount.bills[currentEditingField.denomination.toString()] || 0;
    } else {
      return cashCount.coins[currentEditingField.denomination.toString()] || 0;
    }
  };

  const calculateTotal = (bills: Record<string, number>, coins: Record<string, number>): number => {
    let total = 0;
    
    // Calcular total de billetes
    BILL_DENOMINATIONS.forEach(denom => {
      total += denom * (bills[denom.toString()] || 0);
    });
    
    // Calcular total de monedas
    COIN_DENOMINATIONS.forEach(denom => {
      total += denom * (coins[denom.toString()] || 0);
    });
    
    return total;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Conteo de Efectivo</CardTitle>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="gap-2 h-12 px-6 text-base font-semibold shadow-md transition-all duration-200 active:scale-95 touch-manipulation"
        >
          {isSaving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <Save className="h-5 w-5" />
          )}
          {isSaving ? 'Guardando...' : 'Guardar'}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Columna 1: Billetes */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg text-gray-800 mb-4 flex items-center gap-2">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                💵
              </div>
              Billetes
            </h3>
            {BILL_DENOMINATIONS.map(bill => (
              <div key={bill} className="flex items-center gap-4 p-3 bg-gray-50/50 rounded-lg hover:bg-gray-100/50 transition-colors">
                <div className="flex items-center gap-2 min-w-[80px]">
                  <span className="font-medium text-gray-700">S/</span>
                  <span className="font-bold text-lg text-macartur-primary">{bill}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => decrementBill(bill)}
                    className="w-8 h-8 p-0 flex items-center justify-center"
                    disabled={!cashCount.bills[bill.toString()] || cashCount.bills[bill.toString()] === 0}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <Input
                    type="text"
                    value={cashCount.bills[bill.toString()] || 0}
                    onClick={() => openKeypadForBill(bill)}
                    readOnly
                    className="w-16 text-center font-medium cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => incrementBill(bill)}
                    className="w-8 h-8 p-0 flex items-center justify-center"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <span className="font-semibold text-gray-700 min-w-[120px] text-right">
                  {formatCurrency((cashCount.bills[bill.toString()] || 0) * bill)}
                </span>
              </div>
            ))}
          </div>

          {/* Columna 2: Monedas */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg text-gray-800 mb-4 flex items-center gap-2">
              <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                🪙
              </div>
              Monedas
            </h3>
            {COIN_DENOMINATIONS.map(coin => (
              <div key={coin} className="flex items-center gap-4 p-3 bg-gray-50/50 rounded-lg hover:bg-gray-100/50 transition-colors">
                <div className="flex items-center gap-2 min-w-[80px]">
                  <span className="font-medium text-gray-700">S/</span>
                  <span className="font-bold text-lg text-yellow-600">{coin}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => decrementCoin(coin)}
                    className="w-8 h-8 p-0 flex items-center justify-center"
                    disabled={!cashCount.coins[coin.toString()] || cashCount.coins[coin.toString()] === 0}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <Input
                    type="text"
                    value={cashCount.coins[coin.toString()] || 0}
                    onClick={() => openKeypadForCoin(coin)}
                    readOnly
                    className="w-16 text-center font-medium cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => incrementCoin(coin)}
                    className="w-8 h-8 p-0 flex items-center justify-center"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <span className="font-semibold text-gray-700 min-w-[120px] text-right">
                  {formatCurrency((cashCount.coins[coin.toString()] || 0) * coin)}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Total contado */}
        <div className="mt-8 bg-gradient-to-r from-macartur-primary/10 to-macartur-accent1/10 p-6 rounded-xl border border-macartur-primary/20">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-macartur-primary rounded-full flex items-center justify-center">
                💰
              </div>
              <span className="font-bold text-xl text-gray-800">Total Contado:</span>
            </div>
            <span className="font-bold text-2xl text-macartur-primary">
              {formatCurrency(cashCount.totalCounted)}
            </span>
          </div>
        </div>
      </CardContent>

      {/* Numeric Keypad Modal */}
      {currentEditingField && (
        <NumericKeypad
          isOpen={keypadOpen}
          onClose={() => {
            setKeypadOpen(false);
            setCurrentEditingField(null);
          }}
          onSubmit={handleKeypadSubmit}
          title={currentEditingField.title}
          initialValue={getCurrentValue()}
        />
      )}
    </Card>
  );
};